/* Crystal Quest water report analyzer: the loading boundary (sections/cq-ra-report-analyzer.liquid).
 * Liquid owns the cold landing (H1, words and a working file chooser before any script). The runtime (the shared
 * engine, the Wizard's adapter and screen types, the analysis, the view and the definition) loads on intent: focus or
 * a press on the upload card or a landing button, a dropped file, a picked file. A file picked on the landing goes
 * straight to the upload screen's own file input (a DataTransfer set on it, then its change event), so reading starts
 * without a second pick; a browser that cannot do that sees the upload screen and picks once more.
 * The file is read in this browser by the Wizard's reader and never uploaded; neither it nor its name is stored.
 */
(function () {
  'use strict';
  const QUIZ = 'report-analyzer', STORE = 'cqq:' + QUIZ + ':v1', ENTRANCE = 'cqq:logo-entrance:v1:' + QUIZ;
  function mergePictures(root) {
    // the Wizard's report photographs (script#cqw-images) join the shared picture map before the engine reads it
    const read = el => { try { return el ? JSON.parse(el.textContent || '{}') : null; } catch (_) { return null; } };
    const target = root.querySelector('#cqq-images'), extra = read(root.querySelector('#cqw-images'));
    if (!target || !extra || target.dataset.cqanMerged === 'true') return;
    target.textContent = JSON.stringify(Object.assign(read(target) || {}, extra)); target.dataset.cqanMerged = 'true';
  }
  function init() {
    const root = document.querySelector('[data-cqq-quiz="' + QUIZ + '"]');
    if (!root || root.cqanLoader) return;
    root.cqanLoader = true;
    mergePictures(root);
    // keep the server-printed landing: the engine's landing screen shows this same markup (assets/cq-ra-view.js)
    const stage = root.querySelector('[data-cqq-screen] .cqv-q-stage');
    if (stage && !root.cqanLandingHTML) root.cqanLandingHTML = stage.innerHTML;
    const scope = root.closest('.shopify-section') || document;
    const entries = Array.from(scope.querySelectorAll('script[data-cqan-runtime]'));
    let pending = null, ready = false, queued = null, disposed = false;
    const reveal = () => { if (root.isConnected) root.classList.remove('cqq-fonts-pending'); };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(reveal); else reveal();
    setTimeout(reveal, 1500);
    const status = text => {
      root.querySelectorAll('[data-cqan-land-status]').forEach(p => { p.textContent = text || ''; p.hidden = !text; });
    };
    const busy = on => root.querySelectorAll('[data-cqan-drop]').forEach(b => { if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy'); });
    function script(entry) {
      if (entry.dataset.cqanLoaded === 'true') return Promise.resolve();
      return new Promise((resolve, reject) => {
        const el = document.createElement('script'); el.src = entry.dataset.src; el.async = false;
        let settled = false;
        const timer = setTimeout(() => finish(false), 20000);
        function finish(ok) {
          if (settled) return; settled = true; clearTimeout(timer); el.onload = el.onerror = null;
          if (ok) { entry.dataset.cqanLoaded = 'true'; resolve(); } else { el.remove(); reject(new Error('cqan_load')); }
        }
        el.onload = () => finish(true); el.onerror = () => finish(false);
        entry.parentNode.insertBefore(el, entry);
      });
    }
    const instance = () => window.CQQuizV3 && window.CQQuizV3.instances.find(i => i.root === root);
    function mounted() {
      return new Promise((resolve, reject) => {
        const began = Date.now();
        (function check() {
          if (disposed || !root.isConnected) { reject(new Error('cqan_removed')); return; }
          if (instance()) { resolve(instance()); return; }
          if (Date.now() - began > 20000) { reject(new Error('cqan_mount')); return; }
          setTimeout(check, 30);
        })();
      });
    }
    function load() {
      if (pending) return pending;
      let chain = Promise.resolve();
      entries.forEach(entry => { chain = chain.then(() => script(entry)); });
      pending = chain.then(mounted).then(c => { ready = true; status(''); busy(false); return c; }).catch(e => {
        pending = null; busy(false);
        status('The analyzer could not load. Check your connection and try again, or call 1-800-934-0051.');
        throw e;
      });
      return pending;
    }
    // the engine's start (startQuiz) without the logo entrance: the analyzer opens straight onto its first screen
    function start(c) {
      try { sessionStorage.setItem(ENTRANCE, '1'); } catch (_) {}
      const s = c.state();
      if (s.current !== 'landing') return;
      const btn = root.querySelector('[data-cqq-screen] [data-cqq-do="start"]');
      if (btn) btn.click(); else c.go('u02');
    }
    function handIn(c, file) {
      const input = root.querySelector('[data-cqq-screen="u02"] [data-cqw-file]');
      if (!input) return false;
      try {
        const dt = new DataTransfer(); dt.items.add(file);
        input.files = dt.files;
        if (!input.files || !input.files.length) return false;
        input.dispatchEvent(new Event('change', {bubbles: true}));
        return true;
      } catch (_) { return false; }
    }
    function begin(opts) {
      if (disposed) return;
      queued = opts;
      busy(true); if (!ready) status(opts.typed ? 'Opening the number fields' : 'Getting the reader ready');
      load().then(c => {
        const job = queued; queued = null; if (!job) return;
        start(c);
        if (job.typed) {
          c.commit('u02', {reportEntry: 'typed'});
          c.go('u02b', {history: 'replace'});
          return;
        }
        if (job.file && !handIn(c, job.file)) {
          const note = root.querySelector('[data-cqq-screen="u02"] [data-cqw-note]');
          if (note) note.textContent = 'Choose your file once more to start reading.';
        }
      }).catch(() => { queued = null; });
    }
    // landing controls (event delegation: the same markup before and after the engine mounts). The closing band's
    // "Add your report" ([data-cqan-close], under the first view) opens the landing's own file input.
    const onLanding = el => !!(el && el.closest && el.closest('[data-cqan-land], [data-cqan-close]'));
    root.addEventListener('click', e => {
      const t = e.target; if (!onLanding(t)) return;
      const pick = t.closest('[data-cqan-pick]'), cam = t.closest('[data-cqan-camera]'), typed = t.closest('[data-cqan-typed]');
      if (pick) { e.preventDefault(); const input = root.querySelector('[data-cqan-land] [data-cqan-file]'); if (input) input.click(); load().catch(() => {}); }
      else if (cam) { e.preventDefault(); const input = root.querySelector('[data-cqan-land] [data-cqan-camera-input]'); if (input) input.click(); load().catch(() => {}); }
      else if (typed) { e.preventDefault(); begin({typed: true}); }
    });
    root.addEventListener('change', e => {
      const input = e.target;
      if (!onLanding(input) || !input.matches || !input.matches('[data-cqan-file], [data-cqan-camera-input]')) return;
      const file = input.files && input.files[0]; input.value = '';
      if (file) begin({file});
    });
    root.addEventListener('dragover', e => { const z = onLanding(e.target) && e.target.closest('[data-cqan-drop]'); if (!z) return; e.preventDefault(); z.classList.add('is-over'); });
    root.addEventListener('dragleave', e => { const z = onLanding(e.target) && e.target.closest('[data-cqan-drop]'); if (z && !z.contains(e.relatedTarget)) z.classList.remove('is-over'); });
    root.addEventListener('drop', e => {
      const z = onLanding(e.target) && e.target.closest('[data-cqan-drop]'); if (!z) return;
      e.preventDefault(); z.classList.remove('is-over');
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]; if (file) begin({file});
    });
    // intent: focus or a press on a landing control starts the download, never page load or hover
    const intent = e => { if (onLanding(e.target) && e.target.closest('[data-cqan-intent]')) load().catch(() => {}); };
    root.addEventListener('focusin', intent);
    root.addEventListener('pointerdown', intent);
    scope.addEventListener('shopify:section:unload', () => { disposed = true; }, {once: true});
    // a reload or Back into a started analysis restores it (the engine owns the restore)
    let saved = false;
    try { const s = JSON.parse(sessionStorage.getItem(STORE)); saved = !!(s && s.v === 1 && s.started); } catch (_) {}
    if (saved) load().catch(() => {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
}());
